// Lifted AI Assistant — shared chat hook + UI primitives
// Used by the floating widget, /launchpad/assistant, and /admin/ai-workspace.
import { useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const STORAGE_KEY = "latm_ai_session_v1";

function loadSessionId() {
  try { return window.localStorage.getItem(STORAGE_KEY) || null; }
  catch { return null; }
}
function saveSessionId(id) {
  try { window.localStorage.setItem(STORAGE_KEY, id); } catch {}
}

export function useLiftedAssistant({ endpoint = "/ai/claude/chat", surface = "floating", role } = {}) {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(() => loadSessionId());
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const send = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError("");
    // Optimistic user message
    setMessages((m) => [...m, { role: "user", content: trimmed, ts: Date.now() }]);
    try {
      const { data } = await api.post(endpoint, {
        message: trimmed,
        session_id: sessionIdRef.current,
        surface,
        role,
      });
      if (data?.session_id && data.session_id !== sessionIdRef.current) {
        setSessionId(data.session_id);
        saveSessionId(data.session_id);
      }
      setMessages((m) => [...m, {
        role: "assistant",
        content: data?.reply || "",
        model: data?.model,
        ts: Date.now(),
      }]);
    } catch (e) {
      const code = e?.response?.status;
      const msg = e?.response?.data?.detail
        || (code === 401 ? "Sign in to use the assistant."
           : code === 503 ? "The assistant is taking a quick break. Try again in a moment."
           : "Could not reach the assistant. Please try again.");
      setError(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }, [endpoint, surface, role, busy]);

  const reset = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  return { messages, busy, error, sessionId, send, reset };
}
